import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { RenderContext, type RenderContextValue } from "../core/render-context"
import { ElementRegistry } from "../core/element-registry"
import { registerAllRenderers } from "../renderers"
import { StateManager } from "../runtime/state-manager"
import { DataSourceManager } from "../runtime/datasource-manager"
import { EventDispatcher } from "../runtime/event-dispatcher"
import { ActionFlowEngine } from "../runtime/action-flow-engine"
import { WebPlatformAdapter } from "../platform/web-adapter"
import { validateNamespaces, resolveBindings } from "../runtime/namespace-validator"
import { registerWebMCPTools } from "../runtime/webmcp-bridge"
import { NavigationGuardRunner } from "../runtime/navigation-guard"
import { registerWalletActions } from "../runtime/wallet-actions"
import { ExpressionRegistry } from "../runtime/expression-registry"
import { CogoPage } from "./cogo-page"
import type {
  CogoApp as CogoAppDef,
  CogoBlockDef,
  CogoElement,
  CogoFlow,
  CogoNavigationGuard,
} from "../core/types"
import { loadDesignTokens, getIsolatedAppStyle, type DesignTokens } from "../runtime/design-tokens"

export type ToastParams = { message: string; description?: string; variant?: string }

export interface CogoRuntime {
  stateManager: StateManager
  eventDispatcher: EventDispatcher
  actionEngine: ActionFlowEngine
  dataSourceManager: DataSourceManager
}

export interface CogoAppProps {
  manifest: CogoAppDef
  blocks: Record<string, CogoBlockDef>
  data?: Record<string, unknown>
  flows?: Record<string, CogoFlow>
  designTokens?: DesignTokens
  basePath?: string
  currentRoute?: string
  onNavigate?: (route: string) => void
  onToast?: (params: ToastParams) => void
  onRuntimeReady?: (runtime: CogoRuntime) => void
}

export function CogoApp({
  manifest,
  blocks,
  data,
  flows,
  designTokens,
  basePath,
  currentRoute,
  onNavigate,
  onToast,
  onRuntimeReady,
}: CogoAppProps) {
  const [, forceUpdate] = useState(0)
  const [activeTokens, setActiveTokens] = useState(designTokens)
  const stateRef = useRef<StateManager | null>(null)
  const engineRef = useRef<ActionFlowEngine | null>(null)

  // Flow execution debounce — prevents double-click from firing the same flow twice.
  // Timestamp-based: blocks re-execution within 300ms window.
  const flowTimestampsRef = useRef(new Map<string, number>())
  // Navigate dedup — prevents duplicate navigation within a short window
  const lastNavigateRef = useRef<{ route: string; time: number } | null>(null)
  const activeBlocksRef = useRef<Set<string>>(new Set())

  // Compute current route early so it's available to effects
  const route = currentRoute ?? "/"

  // Build runtime once on mount or when manifest changes
  const { registry, stateManager, dataSourceManager, eventDispatcher, actionEngine } =
    useMemo(() => {
      const reg = new ElementRegistry()
      registerAllRenderers(reg)

      const sm = new StateManager(manifest.globalState ?? {})
      const dsm = new DataSourceManager(manifest.datasources ?? {}, basePath)
      const ed = new EventDispatcher()
      const webAdapter = new WebPlatformAdapter()
      if (onToast) webAdapter.setToastHandler(onToast)
      const ae = new ActionFlowEngine(sm, webAdapter)

      // Wire toast handler — delegates to host app's onToast prop
      ae.onToast = (msg) => {
        if (onToast) {
          onToast(msg)
        }
      }

      // Build guard runner from navigation config
      const guards: CogoNavigationGuard[] = manifest.navigation?.guards ?? []
      const guardRunner = new NavigationGuardRunner(
        guards,
        async (flowName, context) => {
          sm.setState("_guard", { allow: true, redirect: null })
          const flow = flows?.[flowName]
            ?? (flows ? Object.entries(flows).find(([k]) => k.endsWith(`.${flowName}`))?.[1] : undefined)
          if (flow) await ae.executeFlow(flow, context)
        },
        (path) => sm.get(path),
      )

      // Register navigate action with guard check
      ae.registerAction("navigate", async (params) => {
        const route = params.route as string
        if (!route) {
          console.warn("navigate action called without route param:", params)
          return
        }
        const mode = (params.mode as string) || "push"

        // Check guards for push/replace navigations
        if (guards.length > 0 && (mode === "push" || mode === "replace")) {
          const currentRoute = (sm.get("_system.navigation.currentRoute") as string) ?? "/"
          const result = await guardRunner.check(route, currentRoute)
          if (!result.allow) {
            if (result.redirect) {
              if (onNavigate) {
                onNavigate(result.redirect)
              } else if (typeof window !== "undefined") {
                window.history.replaceState(null, "", result.redirect)
                window.dispatchEvent(new PopStateEvent("popstate"))
              }
            }
            return
          }
        }

        if (onNavigate) {
          onNavigate(route)
        } else if (typeof window !== "undefined") {
          window.history.pushState(null, "", route)
          window.dispatchEvent(new PopStateEvent("popstate"))
        }
      })

      // Register loadDataSource action
      ae.registerAction("loadDataSource", async (params) => {
        const source = params.source as string
        const target = params.target as string | undefined
        try {
          const loadedData = await dsm.load(source)
          if (target) {
            sm.setState(target.replace(/^#/, ""), loadedData)
          }
        } catch (err) {
          console.warn(`Failed to load datasource ${source}:`, err)
        }
      })

      // Register compute action for expression steps
      ae.registerAction("compute", (params) => {
        const target = params.target as string
        const expression = params.expression as string
        if (target && expression) {
          const resolved = evaluateExpression(expression, sm)
          sm.setState(target.replace(/^#/, ""), resolved)
        }
      })

      // Register sortByField action — sorts an array in state by a given field
      ae.registerAction("sortByField", (params) => {
        const source = params.source as string
        const target = params.target as string
        const field = params.field as string
        const direction = (params.direction as string) || "asc"
        if (!source || !target || !field) return
        const arr = sm.get(source.replace(/^#/, ""))
        if (!Array.isArray(arr)) return
        const sorted = [...arr].sort((a, b) => {
          const av = a?.[field]
          const bv = b?.[field]
          if (av == null && bv == null) return 0
          if (av == null) return 1
          if (bv == null) return -1
          if (typeof av === "number" && typeof bv === "number") {
            return direction === "asc" ? av - bv : bv - av
          }
          const sa = String(av).toLowerCase()
          const sb = String(bv).toLowerCase()
          if (sa < sb) return direction === "asc" ? -1 : 1
          if (sa > sb) return direction === "asc" ? 1 : -1
          return 0
        })
        sm.setState(target.replace(/^#/, ""), sorted)
      })

      // Register setTheme action — hot-swaps design tokens at runtime
      ae.registerAction("setTheme", async (params) => {
        const themeName = params.theme as string
        if (!themeName) return
        const themes = manifest.themes
        const themePath = themes?.[themeName]
        if (!themePath || !basePath) return

        try {
          const res = await fetch(`${basePath}/${themePath}`)
          if (!res.ok) return
          const raw = await res.json()
          const newTokens = loadDesignTokens(raw)
          setActiveTokens(newTokens)
          sm.setState("_system.theme", themeName)
        } catch (err) {
          console.warn("setTheme failed:", err)
        }
      })

      // Auto-register event bindings from block UI schemas (inline events)
      for (const [blockName, block] of Object.entries(blocks)) {
        const blockNs = block.manifest?.namespace ?? blockName
        walkElements(block.root, (el) => {
          if (el.events) {
            for (const [eventType, handler] of Object.entries(el.events)) {
              if (typeof handler === "string") {
                ed.addBinding(el.id, eventType, { flow: handler, __ns: blockNs })
              } else if (typeof handler === "object" && handler !== null) {
                const h = handler as Record<string, unknown>
                if (h.action === "navigate") {
                  ed.addBinding(el.id, eventType, { flow: `__navigate:${(h.params as Record<string, string>)?.route ?? "/"}`, __ns: blockNs })
                }
              }
            }
          }
        })

        // Register event bindings from block's events.cogo.json
        if (block.events?.bindings) {
          for (const [bindingKey, binding] of Object.entries(block.events.bindings)) {
            // bindingKey format: "elementId:eventType"
            const colonIdx = bindingKey.lastIndexOf(":")
            if (colonIdx > 0) {
              const elementId = bindingKey.slice(0, colonIdx)
              const eventType = bindingKey.slice(colonIdx + 1)
              if (typeof binding === "object" && binding !== null) {
                const b = binding as Record<string, unknown>
                if (b.flow) {
                  ed.addBinding(elementId, eventType, {
                    flow: b.flow as string,
                    params: b.params as Record<string, unknown> | undefined,
                    __ns: blockNs,
                  })
                } else if (b.action === "navigate") {
                  const params = b.params as Record<string, string> | undefined
                  ed.addBinding(elementId, eventType, { flow: `__navigate:${params?.route ?? "/"}`, __ns: blockNs })
                }
              }
            }
          }
        }
      }

      // Initialize block-level state defaults and register block datasources
      for (const block of Object.values(blocks)) {
        if (block.variables?.state) {
          for (const [key, varDef] of Object.entries(block.variables.state)) {
            // Support both formats:
            //   {key: {default: value, type: "string"}}  — schema format
            //   {key: value}                              — direct-value format
            let resolved: unknown
            if (varDef != null && typeof varDef === "object" && !Array.isArray(varDef)) {
              const def = varDef as Record<string, unknown>
              if (def.default !== undefined) {
                resolved = structuredClone(def.default)
              } else if (def.type === undefined) {
                // Plain object without type/default → treat as direct value
                resolved = structuredClone(varDef)
              }
            } else {
              // Primitive or array → direct value
              resolved = structuredClone(varDef)
            }
            if (resolved !== undefined) {
              // Store both bare key AND with "state." prefix for backward compatibility
              // UI JSON may reference as #{billingCycle} or #{state.billingCycle}
              // Deep-merge objects to avoid cross-block state collisions (e.g., multiple blocks use "ui" key)
              const existing = sm.get(key)
              if (existing != null && typeof existing === "object" && !Array.isArray(existing) &&
                  resolved != null && typeof resolved === "object" && !Array.isArray(resolved)) {
                const merged = { ...(existing as Record<string, unknown>), ...(resolved as Record<string, unknown>) }
                sm.setState(key, merged)
                sm.setState(`state.${key}`, structuredClone(merged))
              } else {
                sm.setState(key, resolved)
                sm.setState(`state.${key}`, structuredClone(resolved))
              }
            }
          }
        }
        // localState → "local." prefix
        const localState = (block.variables as Record<string, unknown> | undefined)?.localState
        if (localState && typeof localState === "object") {
          for (const [key, varDef] of Object.entries(localState as Record<string, unknown>)) {
            if (varDef != null && typeof varDef === "object" && !Array.isArray(varDef)) {
              const def = varDef as Record<string, unknown>
              if (def.default !== undefined) {
                sm.setState(`local.${key}`, structuredClone(def.default))
              } else if (def.type === undefined) {
                sm.setState(`local.${key}`, structuredClone(varDef))
              }
            } else {
              sm.setState(`local.${key}`, structuredClone(varDef))
            }
          }
        }
        // props → "props." prefix (safety net for blocks still using #{props.xxx})
        const propsVars = (block.variables as Record<string, unknown> | undefined)?.props
        if (propsVars && typeof propsVars === "object") {
          for (const [key, varDef] of Object.entries(propsVars as Record<string, unknown>)) {
            if (varDef != null && typeof varDef === "object" && !Array.isArray(varDef)) {
              const def = varDef as Record<string, unknown>
              if (def.default !== undefined) {
                sm.setState(`props.${key}`, structuredClone(def.default))
              } else if (def.type === undefined) {
                sm.setState(`props.${key}`, structuredClone(varDef))
              }
            } else {
              sm.setState(`props.${key}`, structuredClone(varDef))
            }
          }
        }
        // computed → evaluate expressions after state is loaded
        const computed = (block.variables as Record<string, unknown> | undefined)?.computed
        if (computed && typeof computed === "object") {
          for (const [key, expr] of Object.entries(computed as Record<string, unknown>)) {
            if (typeof expr === "string") {
              const resolved = sm.resolve(expr)
              sm.setState(`computed.${key}`, resolved)
            }
          }
        }
        if (block.datasource?.sources) {
          for (const [name, source] of Object.entries(block.datasource.sources)) {
            dsm.registerSource(name, source)
            // Auto-load static datasources into state so #{sourceName.field} resolves
            const src = source as unknown as Record<string, unknown>
            if (src.type === "static" && src.data != null) {
              sm.setState(name, structuredClone(src.data))
            }
          }
        }
      }

      // Register shadcn/ui CSS variable defaults so #{theme.card}, #{theme.mutedForeground} etc. resolve.
      // camelCase → kebab-case CSS custom property reference.
      const themeVarMap: Record<string, string> = {
        background: "hsl(var(--background))",
        foreground: "hsl(var(--foreground))",
        card: "hsl(var(--card))",
        cardForeground: "hsl(var(--card-foreground))",
        popover: "hsl(var(--popover))",
        popoverForeground: "hsl(var(--popover-foreground))",
        primary: "hsl(var(--primary))",
        primaryForeground: "hsl(var(--primary-foreground))",
        secondary: "hsl(var(--secondary))",
        secondaryForeground: "hsl(var(--secondary-foreground))",
        muted: "hsl(var(--muted))",
        mutedForeground: "hsl(var(--muted-foreground))",
        accent: "hsl(var(--accent))",
        accentForeground: "hsl(var(--accent-foreground))",
        destructive: "hsl(var(--destructive))",
        destructiveForeground: "hsl(var(--destructive-foreground))",
        border: "hsl(var(--border))",
        input: "hsl(var(--input))",
        ring: "hsl(var(--ring))",
        sidebar: "hsl(var(--sidebar-background, var(--background)))",
        sidebarForeground: "hsl(var(--sidebar-foreground, var(--foreground)))",
        success: "hsl(142 76% 36%)",
        warning: "hsl(38 92% 50%)",
      }
      for (const [key, val] of Object.entries(themeVarMap)) {
        sm.setState(`theme.${key}`, val)
      }

      // Register design tokens in StateManager so #{theme.colors.xxx} resolves
      if (designTokens) {
        const tokenCategories = ["colors", "spacing", "typography", "radius", "shadow"] as const
        for (const cat of tokenCategories) {
          const entries = designTokens[cat]
          if (entries && typeof entries === "object") {
            for (const [key, val] of Object.entries(entries)) {
              sm.setState(`theme.${cat}.${key}`, val)
            }
          }
        }
      }

      // Validate logic block namespaces
      try {
        const nsMap = validateNamespaces(blocks)
        resolveBindings(blocks, nsMap)
      } catch (err) {
        console.error("Block namespace error:", err)
      }

      // Register flows so callFlow action can invoke sub-flows
      if (flows) {
        ae.registerFlows(flows)
      }

      // Register wallet actions if wallet config is provided
      if (manifest.wallet?.baseUrl) {
        registerWalletActions(ae, sm, manifest.wallet)
      }

      // Auto-register WebMCP tools from logic blocks
      if (flows) {
        registerWebMCPTools(blocks, flows, ae, sm)
      }

      // Register all known expressions from block JSON for injection prevention.
      // strict: false → warn-only mode in dev; unregistered expressions still execute.
      const exprRegistry = new ExpressionRegistry({ strict: false })
      for (const blockDef of Object.values(blocks)) {
        exprRegistry.registerFromJson(blockDef)
      }
      // Also register expressions from flows
      if (flows) {
        exprRegistry.registerFromJson(flows)
      }
      // Store on StateManager for downstream access
      ;(sm as unknown as Record<string, unknown>).__exprRegistry = exprRegistry

      stateRef.current = sm
      engineRef.current = ae

      return {
        registry: reg,
        stateManager: sm,
        dataSourceManager: dsm,
        eventDispatcher: ed,
        actionEngine: ae,
      }
    }, [manifest, blocks, basePath])

  // Notify host when runtime is ready (for DevTools, testing, etc.)
  useEffect(() => {
    onRuntimeReady?.({ stateManager, eventDispatcher, actionEngine, dataSourceManager })
  }, [onRuntimeReady, stateManager, eventDispatcher, actionEngine, dataSourceManager])

  // Pre-load external data into state and DSM cache
  useMemo(() => {
    if (data) {
      for (const [key, value] of Object.entries(data)) {
        stateManager.setState(key, value)
        // Pre-populate DSM cache so loadDataSource won't re-fetch
        dataSourceManager.preload(key, value)
      }
      // Re-evaluate computed expressions now that data is loaded
      // (computed was initially evaluated against empty defaults)
      for (const block of Object.values(blocks)) {
        const computed = (block.variables as Record<string, unknown> | undefined)?.computed
        if (computed && typeof computed === "object") {
          for (const [key, expr] of Object.entries(computed as Record<string, unknown>)) {
            if (typeof expr === "string") {
              const resolved = stateManager.resolve(expr)
              stateManager.setState(`computed.${key}`, resolved)
            }
          }
        }
      }
    }
  }, [data, blocks, stateManager, dataSourceManager])

  // Wire event flows
  useEffect(() => {
    if (!flows) return

    // Listen for state changes to trigger re-renders
    const unsub = stateManager.subscribe("", () => {
      forceUpdate((n) => n + 1)
    })

    return unsub
  }, [flows, stateManager])

  // Execute init flows on mount and when route changes
  useEffect(() => {
    if (!flows) return

    // Expose current route in state so sidebar ternary classNames can resolve
    stateManager.setState("_system.navigation.currentRoute", route)

    // Build route context with extracted params
    const matchedPage = manifest.pages.find((p) => matchRoute(p.route, route))
    let routeParams = matchedPage ? extractRouteParams(matchedPage.route, route) : {}

    // For admin routes without named params, extract path segments as navigation context
    // e.g., /admin/financials/transactions/history → { topTabSlug: "financials", categorySlug: "transactions", pageSlug: "history" }
    if (Object.keys(routeParams).length === 0 && route.startsWith("/admin/")) {
      const segments = route.split("/").filter(Boolean) // ["admin", "financials", ...]
      if (segments.length >= 4) {
        routeParams = { topTabSlug: segments[1], categorySlug: segments[2], pageSlug: segments[3] }
      } else if (segments.length >= 3) {
        routeParams = { topTabSlug: segments[1], categorySlug: segments[2] }
      } else if (segments.length >= 2) {
        routeParams = { topTabSlug: segments[1] }
      }
    }

    // Store route params in state so breadcrumb/sidebar computed expressions can access them
    stateManager.setState("_system.navigation.params", routeParams)

    // Expose matched page name for breadcrumb display
    if (matchedPage) {
      stateManager.setState("_system.navigation.pageName", matchedPage.name ?? matchedPage.title ?? matchedPage.id)
    }

    // Sync activeTopTab from URL for direct navigation (sidebar/topnav depend on this)
    if (routeParams.topTabSlug) {
      const currentTab = stateManager.get("_system.navigation.activeTopTab") as string | undefined
      if (currentTab !== routeParams.topTabSlug) {
        stateManager.setState("_system.navigation.activeTopTab", routeParams.topTabSlug)
      }
    }

    const context: Record<string, unknown> = { route: { params: routeParams, path: route } }

    // Only execute init flows for blocks on the current page (+ global + layout blocks).
    // Without this, ALL 56+ init flows fire concurrently on every page, causing
    // state collisions (e.g., partner-payment-list overwrites payment-history's ui.transactions).
    const activeBlocks = new Set<string>(manifest.globalBlocks ?? [])
    if (matchedPage) {
      for (const blockName of matchedPage.blocks) {
        activeBlocks.add(blockName)
      }
      const pageLayout = (matchedPage as unknown as Record<string, unknown>).layout as string | undefined
      if (pageLayout) activeBlocks.add(pageLayout)
    }
    activeBlocksRef.current = activeBlocks

    // Re-apply current page's block variable defaults so they take precedence
    // over the global merge. All blocks' "ui" defaults are deep-merged at init time,
    // so a non-active block may have overwritten keys like "activeTab".
    for (const blockName of activeBlocks) {
      const block = blocks[blockName]
      if (!block?.variables?.state) continue
      for (const [key, varDef] of Object.entries(block.variables.state)) {
        let resolved: unknown
        if (varDef != null && typeof varDef === "object" && !Array.isArray(varDef)) {
          const def = varDef as Record<string, unknown>
          if (def.default !== undefined) resolved = structuredClone(def.default)
          else if (def.type === undefined) resolved = structuredClone(varDef)
        } else {
          resolved = structuredClone(varDef)
        }
        if (resolved !== undefined) {
          const existing = stateManager.get(key)
          if (existing != null && typeof existing === "object" && !Array.isArray(existing) &&
              resolved != null && typeof resolved === "object" && !Array.isArray(resolved)) {
            const merged = { ...(existing as Record<string, unknown>), ...(resolved as Record<string, unknown>) }
            stateManager.setState(key, merged)
          } else {
            stateManager.setState(key, resolved)
          }
        }
      }
    }

    const flowEntries = Object.entries(flows)
    for (const [name, flow] of flowEntries) {
      if (flow.trigger === "init") {
        const blockName = name.split(".")[0]
        if (activeBlocks.size === 0 || activeBlocks.has(blockName)) {
          // Init flow fires for this block
          actionEngine.executeFlow(flow, context).catch((err) =>
            console.warn("Init flow error:", err),
          )
        }
      }
    }
  }, [flows, actionEngine, route, manifest.pages])

  const dispatchEvent = useCallback(
    (elementId: string, eventType: string, payload?: unknown) => {
      eventDispatcher.dispatch(elementId, eventType, payload)

      // Extract item context from payload (passed by renderers inside block-ref iterations)
      const payloadObj = (payload != null && typeof payload === "object") ? payload as Record<string, unknown> : {}

      // Check if there's a flow binding for this event (scoped to active page blocks)
      const binding = eventDispatcher.getBinding(elementId, eventType, activeBlocksRef.current)
      const flowName = binding?.flow
      if (flowName) {
        // Handle built-in navigate action (with dedup guard)
        if (flowName.startsWith("__navigate:")) {
          const resolvedRoute = payloadObj.__resolvedRoute as string | undefined
          const route = resolvedRoute || flowName.slice("__navigate:".length)
          // Prevent duplicate navigation within 300ms
          const now = Date.now()
          const last = lastNavigateRef.current
          if (last && last.route === route && now - last.time < 300) return
          lastNavigateRef.current = { route, time: now }
          if (onNavigate) onNavigate(route)
          return
        }
        // Execute the named flow (debounce guard for tap events only — not text input)
        // Resolve flow: try block-scoped name first, then suffix match as fallback
        const blockNs = binding.__ns
        const resolvedFlow = (blockNs ? flows?.[`${blockNs}.${flowName}`] : undefined)
          ?? flows?.[flowName]
          ?? (flows ? Object.entries(flows).find(([k]) => k.endsWith(`.${flowName}`))?.[1] : undefined)
        if (resolvedFlow) {
          if (eventType === "tap") {
            const lockKey = `${elementId}:${eventType}`
            const now = Date.now()
            const lastExec = flowTimestampsRef.current.get(lockKey)
            if (lastExec && now - lastExec < 300) return
            flowTimestampsRef.current.set(lockKey, now)
          }

          // Build flow context from item context (e.g., { product: {...} })
          const { __resolvedRoute: _, ...itemContext } = payloadObj

          // Raw event payload (always preserved so flows can access #{event.value})
          const rawEvent = (payload != null && typeof payload === "object")
            ? payload as Record<string, unknown>
            : { value: payload }

          // Resolve binding params with event in context so #{event.value} works
          const eventParams = binding.params
            ? { ...rawEvent, ...resolveBindingParams(binding.params, { ...itemContext, event: rawEvent }) }
            : payload

          const flowContext: Record<string, unknown> = { event: eventParams, ...itemContext }
          actionEngine.executeFlow(resolvedFlow, flowContext).then(() => {
            forceUpdate((n) => n + 1)
          })
        }
      }
    },
    [eventDispatcher, flows, actionEngine, onNavigate],
  )

  const resolveSymbol = useCallback(
    (expr: string) => stateManager.resolve(expr),
    [stateManager],
  )

  const ctx = useMemo<RenderContextValue>(
    () => ({
      registry,
      resolveSymbol,
      dispatchEvent,
      mode: "preview",
      blocks,
      designTokens: activeTokens,
    }),
    [registry, resolveSymbol, dispatchEvent, blocks, activeTokens],
  )

  // Fully-isolated app theme — always returns a complete CSS variable set so
  // the app content is independent of the Studio shell's dark/light toggle.
  const tokenStyle = useMemo(
    () => getIsolatedAppStyle(activeTokens ?? undefined, 'light'),
    [activeTokens],
  )

  // Route matching — redirect to navigation.defaultRoute if no page matches current route
  const matchedPage = manifest.pages.find((p) => matchRoute(p.route, route))

  useEffect(() => {
    if (!matchedPage && manifest.navigation?.defaultRoute) {
      const defaultRoute = manifest.navigation.defaultRoute
      if (onNavigate) {
        onNavigate(defaultRoute)
      } else if (typeof window !== "undefined") {
        window.history.replaceState(null, "", defaultRoute)
        window.dispatchEvent(new PopStateEvent("popstate"))
      }
    }
  }, [matchedPage, manifest.navigation, onNavigate])

  if (!matchedPage) {
    return <div className="flex items-center justify-center min-h-[200px] text-muted-foreground">Page not found</div>
  }

  return (
    <div className="bg-background text-foreground" style={tokenStyle as React.CSSProperties}>
      <RenderContext.Provider value={ctx}>
        <CogoPage page={matchedPage} blocks={blocks} navigationType={(manifest.navigation as Record<string, unknown> | undefined)?.type as string | undefined} />
      </RenderContext.Provider>
    </div>
  )
}

function matchRoute(pattern: string, path: string): boolean {
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")

  if (patternParts.length !== pathParts.length) return false

  return patternParts.every((part, i) => {
    if (part.startsWith(":")) return true
    return part === pathParts[i]
  })
}

/** Extract route params (e.g., /store/product/:id from /store/product/123 → { id: "123" }) */
export function extractRouteParams(
  pattern: string,
  path: string,
): Record<string, string> {
  const params: Record<string, string> = {}
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")

  patternParts.forEach((part, i) => {
    if (part.startsWith(":") && pathParts[i]) {
      params[part.slice(1)] = pathParts[i]
    }
  })

  return params
}

function walkElements(element: CogoElement | undefined | null, fn: (el: CogoElement) => void): void {
  if (!element) return
  fn(element)
  element.children?.forEach((child) => walkElements(child, fn))
}

/**
 * Resolve binding params: evaluate #{...} expressions against item context.
 * Supports dot-path lookup (#{item.id}) and arithmetic (#{item.quantity + 1}).
 */
function resolveBindingParams(
  params: Record<string, unknown>,
  context: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      const pureMatch = /^#\{([^}]+)\}$/.exec(value)
      if (pureMatch) {
        const inner = pureMatch[1].trim()
        result[key] = evaluateContextExpression(inner, context)
      } else {
        result[key] = value
      }
    } else {
      result[key] = value
    }
  }
  return result
}

/**
 * Evaluate an expression against context.
 * Resolves variable references from context and evaluates using safe arithmetic.
 */
function evaluateContextExpression(expr: string, context: Record<string, unknown>): unknown {
  // Simple dot-path: "item.id"
  const pathValue = getFromContext(expr, context)
  if (pathValue !== undefined) return pathValue

  // Expression with operators: "item.quantity + 1"
  // Replace variable references with their numeric/string values
  let processed = expr
  const refs: Array<{ match: string; value: unknown }> = []
  processed = expr.replace(/([a-zA-Z_][\w]*(?:\.[a-zA-Z_][\w]*)*)/g, (match) => {
    const val = getFromContext(match, context)
    if (val !== undefined) {
      refs.push({ match, value: val })
      if (typeof val === "number") return val.toString()
      if (typeof val === "string") {
        const n = parseFloat(val)
        return isNaN(n) ? JSON.stringify(val) : n.toString()
      }
      return String(val)
    }
    return match
  })

  if (refs.length === 0) return undefined

  // If only one ref and the entire expression IS that ref, return the raw value
  if (refs.length === 1 && expr.trim() === refs[0].match) {
    return refs[0].value
  }

  // Try simple arithmetic evaluation
  const trimmed = processed.trim()
  const numResult = parseFloat(trimmed)
  if (!isNaN(numResult) && /^-?\d+(\.\d+)?$/.test(trimmed)) return numResult

  // Handle simple binary operations: a + b, a - b, a * b, a / b
  const binMatch = /^(-?\d+(?:\.\d+)?)\s*([+\-*/])\s*(-?\d+(?:\.\d+)?)$/.exec(trimmed)
  if (binMatch) {
    const l = parseFloat(binMatch[1])
    const r = parseFloat(binMatch[3])
    switch (binMatch[2]) {
      case "+": return l + r
      case "-": return l - r
      case "*": return l * r
      case "/": return r !== 0 ? l / r : 0
    }
  }

  return refs[0]?.value
}

function getFromContext(path: string, ctx: Record<string, unknown>): unknown {
  const keys = path.split(".")
  let current: unknown = ctx
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

/**
 * Evaluate a compute expression using ComputeEvaluator (safe, no new Function()).
 * Expressions use `#path.to.value` to reference state values.
 */
function evaluateExpression(expression: string, sm: StateManager): unknown {
  return sm.getCompute().evaluate(expression)
}
